import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const Demo = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [currentStep, setCurrentStep] = useState(0);
  const [simState, setSimState] = useState('success');

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'Info' },
    { id: 'workflow', label: 'How It Works', icon: 'Zap' },
    { id: 'data', label: 'Sample Data', icon: 'Database' },
    { id: 'states', label: 'Simulated States', icon: 'Activity' },
    { id: 'faq', label: 'FAQ', icon: 'HelpCircle' }
  ];

  const workflowSteps = [
    { title: 'Digitize', desc: 'Scan garments via AI vision or sync digital receipts.', icon: 'Camera' },
    { title: 'Catalog', desc: 'Auto-tagging for fabric, silhouette, and season.', icon: 'Tag' },
    { title: 'Curate', desc: 'Build modular capsule wardrobes for travel or work.', icon: 'Briefcase' },
    { title: 'Sync', desc: 'Integrate local weather and calendar commitments.', icon: 'CloudRain' },
    { title: 'Style', desc: 'Compose looks on the interactive Outfit Canvas.', icon: 'Edit3' },
    { title: 'Optimize', desc: 'Track cost-per-wear and utilization metrics.', icon: 'TrendingUp' }
  ];

  return (
    <div className="max-w-5xl mx-auto space-y-12 pb-20">
      {/* Header */}
      <header className="space-y-4 border-b border-pearl-200 pb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-graphite-900 text-white flex items-center justify-center font-editorial italic text-xl">W</div>
          <h1 className="font-editorial text-4xl">Product Demo</h1>
        </div>
        <p className="text-graphite-500 font-mono text-sm uppercase tracking-widest">Interactive System Specification • v2.4.1</p>
      </header>

      {/* Navigation Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide border-b border-pearl-100">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-6 py-3 text-xs font-mono uppercase tracking-widest transition-all whitespace-nowrap ${
              activeTab === tab.id 
                ? 'bg-graphite-900 text-white' 
                : 'text-graphite-400 hover:text-graphite-900'
            }`}
          >
            <SafeIcon name={tab.icon} className="w-3 h-3" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content Area */}
      <div className="min-h-[500px]">
        <AnimatePresence mode="wait">
          {activeTab === 'overview' && (
            <motion.div 
              key="overview"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center"
            >
              <div className="space-y-6">
                <h2 className="font-editorial text-3xl">The Problem: Closet Blindness</h2>
                <p className="text-graphite-600 leading-relaxed">
                  Most users wear only 20% of their wardrobe 80% of the time. WardrobeWave solves "closet blindness" by creating a digital twin of your physical archive.
                </p>
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <div className="w-1 bg-runway-500" />
                    <div>
                      <p className="font-bold text-sm uppercase">Main Outcome</p>
                      <p className="text-sm text-graphite-500">Zero-friction morning styling and sustainable garment lifecycle management.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-pearl-100 aspect-square flex items-center justify-center p-8 border border-pearl-200">
                <div className="text-center space-y-4">
                  <SafeIcon name="Shield" className="w-12 h-12 mx-auto text-runway-500" />
                  <p className="font-mono text-[10px] uppercase tracking-widest text-graphite-400">Encrypted • Private • Local First</p>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'workflow' && (
            <motion.div 
              key="workflow"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-8"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {workflowSteps.map((step, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentStep(i)}
                    className={`p-6 border text-left transition-all ${
                      currentStep === i ? 'border-graphite-900 bg-white ring-1 ring-graphite-900' : 'border-pearl-200 bg-pearl-50/50 grayscale hover:grayscale-0'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <SafeIcon name={step.icon} className={`w-6 h-6 ${currentStep === i ? 'text-runway-500' : 'text-graphite-400'}`} />
                      <span className="font-mono text-[10px] text-graphite-300">0{i+1}</span>
                    </div>
                    <h3 className="font-bold text-xs uppercase tracking-widest mb-2">{step.title}</h3>
                    <p className="text-[11px] text-graphite-500 leading-normal">{step.desc}</p>
                  </button>
                ))}
              </div>
              <div className="bg-graphite-900 text-white p-8 md:p-12 text-center space-y-4">
                <p className="font-editorial text-2xl italic">Step Outcome: {workflowSteps[currentStep].title}</p>
                <div className="max-w-md mx-auto h-1 bg-pearl-800">
                  <motion.div 
                    className="h-full bg-runway-500"
                    animate={{ width: `${((currentStep + 1) / workflowSteps.length) * 100}%` }}
                  />
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'data' && (
            <motion.div 
              key="data"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-8"
            >
              <div className="overflow-x-auto border border-pearl-200">
                <table className="w-full text-left text-xs font-mono">
                  <thead className="bg-pearl-100 border-b border-pearl-200">
                    <tr>
                      <th className="p-4 uppercase tracking-tighter">Asset ID</th>
                      <th className="p-4 uppercase tracking-tighter">Brand</th>
                      <th className="p-4 uppercase tracking-tighter">Status</th>
                      <th className="p-4 uppercase tracking-tighter">Worn Count</th>
                      <th className="p-4 uppercase tracking-tighter">CPW</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-pearl-100 bg-white">
                    {[
                      { id: 'WW-102', brand: 'Celine', status: 'In-Use', count: 12, cpw: '$37.50' },
                      { id: 'WW-105', brand: 'The Row', status: 'Archived', count: 4, cpw: '$222.50' },
                      { id: 'WW-109', brand: 'Burberry', status: 'In-Use', count: 8, cpw: '$150.00' },
                      { id: 'WW-112', brand: 'Khaite', status: 'Lent Out', count: 22, cpw: '$32.70' }
                    ].map(row => (
                      <tr key={row.id} className="hover:bg-pearl-50">
                        <td className="p-4 text-graphite-900">{row.id}</td>
                        <td className="p-4 font-bold">{row.brand}</td>
                        <td className="p-4">
                          <span className="px-2 py-0.5 bg-pearl-100 rounded-full">{row.status}</span>
                        </td>
                        <td className="p-4">{row.count}x</td>
                        <td className="p-4 text-runway-500 font-bold">{row.cpw}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="glass-panel p-6 border-l-4 border-l-runway-500">
                  <h4 className="text-[10px] font-mono uppercase text-graphite-400 mb-2">Weather Note (Sample)</h4>
                  <p className="font-editorial text-sm italic">"Heavy rain expected. Swapping Leather Suede for Gore-Tex Shell. Maintaining silhouette via tailored trousers."</p>
                </div>
                <div className="glass-panel p-6 border-l-4 border-l-graphite-900">
                  <h4 className="text-[10px] font-mono uppercase text-graphite-400 mb-2">Packing List (Sample)</h4>
                  <p className="text-xs">Paris Fashion Week • 5 Days • 12 Assets • 22 Looks Possible</p>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'states' && (
            <motion.div 
              key="states"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-8"
            >
              {/* Interaction Control */}
              <div className="flex gap-4 justify-center bg-pearl-100 p-4 border border-pearl-200">
                {['success', 'loading', 'warning', 'empty'].map(s => (
                  <button 
                    key={s}
                    onClick={() => setSimState(s)}
                    className={`px-4 py-1 text-[10px] font-mono uppercase border transition-all ${
                      simState === s ? 'bg-graphite-900 text-white border-graphite-900' : 'bg-white text-graphite-400 border-pearl-200'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>

              <div className="bg-white border border-pearl-200 aspect-[21/9] flex items-center justify-center p-12">
                <AnimatePresence mode="wait">
                  {simState === 'success' && (
                    <motion.div key="s" initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center space-y-3">
                      <div className="w-12 h-12 bg-green-100 text-green-500 rounded-full flex items-center justify-center mx-auto"><SafeIcon name="Check" /></div>
                      <p className="font-mono text-xs uppercase tracking-widest">Asset Synchronized</p>
                    </motion.div>
                  )}
                  {simState === 'loading' && (
                    <motion.div key="l" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center space-y-3">
                      <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: 'linear' }} className="w-12 h-12 border-2 border-runway-500 border-t-transparent rounded-full mx-auto" />
                      <p className="font-mono text-xs uppercase tracking-widest">Analyzing Silhouette...</p>
                    </motion.div>
                  )}
                  {simState === 'warning' && (
                    <motion.div key="w" initial={{ x: 10, opacity: 0 }} animate={{ x: 0, opacity: 1 }} className="text-center space-y-3">
                      <div className="w-12 h-12 bg-yellow-100 text-yellow-600 rounded-full flex items-center justify-center mx-auto"><SafeIcon name="AlertTriangle" /></div>
                      <p className="font-mono text-xs uppercase tracking-widest">Metadata Conflict Detected</p>
                    </motion.div>
                  )}
                  {simState === 'empty' && (
                    <motion.div key="e" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center space-y-3">
                      <SafeIcon name="Box" className="w-12 h-12 mx-auto text-pearl-300" />
                      <p className="font-mono text-xs uppercase tracking-widest">No Assets in Category</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}

          {activeTab === 'faq' && (
            <motion.div 
              key="faq"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-4"
            >
              {[
                { q: "How does the AI recognize my clothes?", a: "We use a proprietary vision model trained on over 5M fashion items to detect cut, fabric, and brand identifiers from simple smartphone photos." },
                { q: "Is my closet data private?", a: "Yes. All images are processed securely and we never sell your style data. Your digital archive belongs solely to you." },
                { q: "Do I need to manualy enter prices?", a: "No. Our receipt sync integrations (Shopify, Gmail) automatically fetch price and purchase date to calculate Cost-Per-Wear." },
                { q: "Can I share my closet with a stylist?", a: "Yes, the Premium plan allows for 'Guest Access' tokens so stylists can build looks directly on your canvas." },
                { q: "How accurate is the weather-aware engine?", a: "We sync with local 10-day forecasts every hour to suggest fabric swaps (e.g., silk to cotton) based on humidity and temp." }
              ].map((item, i) => (
                <details key={i} className="group border border-pearl-200 bg-white">
                  <summary className="p-6 cursor-pointer flex justify-between items-center list-none font-bold text-sm uppercase tracking-tight">
                    {item.q}
                    <SafeIcon name="ChevronDown" className="w-4 h-4 group-open:rotate-180 transition-transform" />
                  </summary>
                  <div className="px-6 pb-6 text-sm text-graphite-500 leading-relaxed border-t border-pearl-50 pt-4">
                    {item.a}
                  </div>
                </details>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <footer className="pt-10 border-t border-pearl-200 text-center">
        <p className="text-[10px] font-mono text-graphite-400 uppercase tracking-[0.3em]">
          Made by LingLink AI and DigLab AI
        </p>
      </footer>
    </div>
  );
};

export default Demo;